import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { CircularProgress } from "@mui/material";
import { UntappdEventResponse } from "../models/UntappdEventResponse";

export const EventsMarquee = () => {
  const maxEventDuration = 7 * 4 * 3; // 3 months
  const [eventData, setEventData] = useState<UntappdEventResponse | null>(null);
  const [isEventLoading, setIsEventLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [failedImageUrls, setFailedImageUrls] = useState<string[]>([]);

  const etagRef = useRef<string | null>(null);
  const lastModifiedRef = useRef<string | null>(null);
  const fetchingRef = useRef(false);
  const initialLoadRef = useRef(true);
  const cycleTimeRef = useRef<number>(20000);

  // rotation from query string
  const rotationDegrees = useMemo(() => {
    if (typeof window === "undefined") return 0;
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get("rotation");
      if (!v) return 0;
      const p = parseInt(v, 10);
      if (!Number.isFinite(p)) return 0;
      return ((p % 360) + 360) % 360;
    } catch {
      return 0;
    }
  }, []);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgScale, setImgScale] = useState<number>(1);

  const scaleOverride = useMemo(() => {
    if (typeof window === "undefined") return 1;
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get("scale");
      if (!v) return 1;
      const p = parseFloat(v);
      if (!Number.isFinite(p)) return 1;
      return Math.max(0.1, p);
    } catch {
      return 1;
    }
  }, []);

  const computeScaleToFit = useCallback(() => {
    const img = imgRef.current;
    const container = containerRef.current;
    if (!img || !container) return;

    const cw = container.clientWidth - 24;
    const ch = container.clientHeight - 24;
    const containerAspect = cw / ch;
    const targetAspect = 16 / 9;

    const w = img.naturalWidth || img.width || 1;
    const h = img.naturalHeight || img.height || 1;

    const rad = (rotationDegrees * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));

    const bboxW = w * cos + h * sin;
    const bboxH = w * sin + h * cos;

    let baseScale = Math.min(cw / bboxW, ch / bboxH) || 1;

    // For a 16:9 display, prefer filling the viewport more naturally.
    if (containerAspect > targetAspect) {
      baseScale = Math.min(baseScale, ch / bboxH || 1);
    } else {
      baseScale = Math.min(baseScale, cw / bboxW || 1);
    }

    const finalScale = baseScale * (scaleOverride || 1);
    setImgScale(finalScale);
  }, [rotationDegrees, scaleOverride]);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_UNTAPPD_API_BASE_URL;
    const user = process.env.REACT_APP_UNTAPPD_API_USER;
    const apikey = process.env.REACT_APP_UNTAPPD_API_KEY;
    const locationId = process.env.REACT_APP_UNTAPPD_LOCATION_ID;

    const fetchData = async () => {
      try {
        setIsEventLoading(true);
        const response = await axios.get(
          `${baseUrl}locations/${locationId}/events`,
          {
            auth: { username: user!, password: apikey! },
          }
        );
        setEventData(response.data as UntappdEventResponse);
        setFailedImageUrls([]);
        const etag = response.headers?.etag as string | undefined;
        const lastModified = response.headers?.["last-modified"] as
          | string
          | undefined;
        if (etag) etagRef.current = etag;
        if (lastModified) lastModifiedRef.current = lastModified;
      } catch (error) {
        console.log("Error", error);
      } finally {
        initialLoadRef.current = false;
        setIsEventLoading(false);
      }
    };

    fetchData();
  }, []);

  const eventImages = useMemo(() => {
    if (!eventData?.events?.length)
      return [] as Array<{ id: number; title: string; image_url: string }>;

    const now = dayjs();
    const eventMaxDate = dayjs().add(maxEventDuration, "day");

    return eventData.events
      .filter(
        (ev) =>
          ev.image_url &&
          dayjs(ev.start_time).isAfter(now.subtract(1, "day")) &&
          dayjs(ev.start_time).isBefore(eventMaxDate)
      )
      .sort((a, b) => dayjs(a.start_time).diff(dayjs(b.start_time)))
      .map((ev) => ({
        id: ev.id,
        title: ev.name,
        image_url: ev.image_url as string,
      }));
  }, [eventData]);

  const visibleEventImages = useMemo(
    () => eventImages.filter((img) => !failedImageUrls.includes(img.image_url)),
    [eventImages, failedImageUrls]
  );

  const isDefaultTwoUpLayout = useMemo(
    () => rotationDegrees === 0 && scaleOverride === 1,
    [rotationDegrees, scaleOverride]
  );

  const displayedImageIndexes = useMemo(() => {
    if (!visibleEventImages.length) return [] as number[];

    if (!isDefaultTwoUpLayout) return [activeIndex];

    const startIndex = activeIndex % visibleEventImages.length;
    const shouldShowSingle =
      visibleEventImages.length % 2 === 1 &&
      startIndex === visibleEventImages.length - 1;

    return shouldShowSingle
      ? [startIndex]
      : [startIndex, (startIndex + 1) % visibleEventImages.length];
  }, [activeIndex, isDefaultTwoUpLayout, visibleEventImages]);

  const displayedEventImages = useMemo(
    () =>
      displayedImageIndexes
        .map((index) => visibleEventImages[index])
        .filter((img): img is (typeof visibleEventImages)[number] =>
          Boolean(img)
        ),
    [displayedImageIndexes, visibleEventImages]
  );

  useEffect(() => {
    if (!visibleEventImages.length) {
      setActiveIndex(0);
      return;
    }
    if (activeIndex >= visibleEventImages.length) {
      setActiveIndex(0);
      return;
    }

    const step = isDefaultTwoUpLayout && visibleEventImages.length > 1 ? 2 : 1;
    const interval = window.setInterval(() => {
      setActiveIndex((i) => (i + step) % visibleEventImages.length);
    }, cycleTimeRef.current);

    return () => window.clearInterval(interval);
  }, [visibleEventImages.length, activeIndex, isDefaultTwoUpLayout]);

  const hasStartedCyclingRef = useRef(false);
  const prevIndexRef = useRef<number | null>(null);

  const refetchEvents = useCallback(async () => {
    const baseUrl = process.env.REACT_APP_UNTAPPD_API_BASE_URL;
    const user = process.env.REACT_APP_UNTAPPD_API_USER;
    const apikey = process.env.REACT_APP_UNTAPPD_API_KEY;
    const locationId = process.env.REACT_APP_UNTAPPD_LOCATION_ID;

    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      const headers: Record<string, string> = {};
      if (etagRef.current) headers["If-None-Match"] = etagRef.current;
      if (lastModifiedRef.current)
        headers["If-Modified-Since"] = lastModifiedRef.current;

      const response = await axios.get(
        `${baseUrl}locations/${locationId}/events`,
        {
          headers,
          validateStatus: (s) => s === 200 || s === 304,
          auth: { username: user!, password: apikey! },
        }
      );

      if (response.status === 200) {
        setEventData(response.data as UntappdEventResponse);
        setFailedImageUrls([]);
        const etag = response.headers?.etag as string | undefined;
        const lastModified = response.headers?.["last-modified"] as
          | string
          | undefined;
        if (etag) etagRef.current = etag;
        if (lastModified) lastModifiedRef.current = lastModified;
      }
    } catch (error) {
      console.log("Error refetching events", error);
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!visibleEventImages.length) return;
    if (fetchingRef.current) return;
    refetchEvents();
  }, [activeIndex, visibleEventImages.length, refetchEvents]);

  // preload next image
  useEffect(() => {
    if (!visibleEventImages.length) return;
    const next =
      visibleEventImages[(activeIndex + 1) % visibleEventImages.length];
    if (!next) return;
    const img = new Image();
    img.src = next.image_url;
  }, [activeIndex, visibleEventImages]);

  useEffect(() => {
    if (!visibleEventImages.length) {
      prevIndexRef.current = null;
      hasStartedCyclingRef.current = false;
      return;
    }

    if (!hasStartedCyclingRef.current && activeIndex > 0)
      hasStartedCyclingRef.current = true;

    if (
      hasStartedCyclingRef.current &&
      prevIndexRef.current === visibleEventImages.length - 1 &&
      activeIndex === 0
    ) {
      refetchEvents();
    }

    prevIndexRef.current = activeIndex;
  }, [activeIndex, visibleEventImages.length, refetchEvents]);

  const handleImageError = (imageUrl: string) => {
    setFailedImageUrls((prev) => {
      if (prev.includes(imageUrl)) return prev;

      const next = [...prev, imageUrl];
      const remaining = eventImages.filter((i) => !next.includes(i.image_url));
      if (!remaining.length) {
        setActiveIndex(0);
        return next;
      }

      if (isDefaultTwoUpLayout) {
        setActiveIndex((i) => (i + 2) % visibleEventImages.length);
      } else {
        setActiveIndex((i) => (i + 1) % visibleEventImages.length);
      }

      return next;
    });
  };

  useEffect(() => {
    if (isDefaultTwoUpLayout) {
      setImgScale(1);
      return;
    }

    computeScaleToFit();
    const onResize = () => computeScaleToFit();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [
    computeScaleToFit,
    isDefaultTwoUpLayout,
    activeIndex,
    rotationDegrees,
    scaleOverride,
  ]);

  if (isEventLoading) {
    return (
      <div className="min-h-screen min-w-full flex items-center justify-center bg-black">
        <CircularProgress color="inherit" />
      </div>
    );
  }

  if (!visibleEventImages.length) {
    return (
      <div className="min-h-screen min-w-full flex items-center justify-center bg-black text-white">
        No event images available.
      </div>
    );
  }

  const activeImage = visibleEventImages[activeIndex];

  return (
    <div
      ref={containerRef}
      className="h-screen w-screen bg-black overflow-hidden relative"
    >
      {isDefaultTwoUpLayout ? (
        <div className="absolute inset-0 flex items-stretch justify-center gap-[0.75vw] px-[0.75vw]">
          {displayedEventImages.map((image, index) => (
            <img
              key={`${image.id}-${index}`}
              src={image.image_url}
              alt={image.title}
              onError={() => handleImageError(image.image_url)}
              style={{
                display: "block",
                height: "100%",
                width: "50%",
                maxWidth: "calc(50% - 0.375vw)",
                objectFit: "cover",
                objectPosition: "center",
                borderRadius: 0,
                flex: "1 1 0",
              }}
            />
          ))}
        </div>
      ) : (
        <img
          ref={imgRef}
          src={activeImage.image_url}
          alt={activeImage.title}
          onLoad={() => computeScaleToFit()}
          onError={() => handleImageError(activeImage.image_url)}
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: `translate(-50%, -50%) rotate(${rotationDegrees}deg) scale(${imgScale})`,
            transformOrigin: "center center",
            display: "block",
            width: "auto",
            height: "auto",
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            objectPosition: "center",
          }}
        />
      )}
    </div>
  );
};

export default EventsMarquee;
